import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { pathApi } from '../api/path';
import { storiesApi } from '../api/stories';
import { useSyncStoryContext } from '../hooks/useSyncStoryContext';
import StoryPicker from '../components/Common/StoryPicker';
import AtlasIcon from '../components/icons/AtlasIcon';
import { plottrackPath } from '../utils/plottrackPath';
import { bookNotesPath } from '../utils/seriesNotes';
import { pathRoomPath } from '../utils/pathRoomPath';
import { bringIntoBookPath } from '../utils/bringIntoBookPath';
import { rememberTasksReturn } from '../utils/handoffReturn';
import { continueDraftingPath, reviewDraftPath } from '../utils/sceneProsePath';
import { draftThisScenePath } from '../utils/sceneWritingPath';
import { bringIntoBookApi } from '../api/bringIntoBook';
import {
  PATH_ACTION_LABELS,
  PATH_ALL_PRESENT,
  PATH_GAPS_ACTION,
  PATH_GAPS_CONTROL,
  PATH_GAPS_EMPTY,
  PATH_HEADER,
  PATH_ROW_IDS,
  PATH_ROW_NAMES,
  PATH_STATE_LABELS,
  PATH_TALK_OR_NOTES_NAMES,
  PATH_TALK_OR_NOTES_TEACHING,
  PATH_TEACHING_LINES,
} from './pathCopy';
import { WORKSHOP_STATUS_NOT_READY } from '../utils/plotReadyCopy';
import BookStructureDoor from '../components/Frameworks/BookStructureDoor';
import './PathPage.css';

const unwrapList = (res) => (Array.isArray(res) ? res : res?.data || []);

const byRecency = (a, b) => {
  const at = new Date(a.last_activity_at || a.created_at).getTime();
  const bt = new Date(b.last_activity_at || b.created_at).getTime();
  return bt - at;
};

const catalogNo = (index) => `NO. ${String(index + 1).padStart(2, '0')}`;

function rowAction(row, { fromImport, peopleBring }) {
  if (!row) return null;
  if (row.id === 'scene_prose') {
    return row.state === 'later' ? null : (row.action || 'scene_writing');
  }
  if (row.id === 'talk_or_notes' && row.state === 'missing' && fromImport) {
    return 'notes';
  }
  if (row.id === 'people_and_places' && peopleBring) {
    return 'bring_into_book';
  }
  return row.action || null;
}

/**
 * PathPage — book-scoped Tasks room. Presence only. Does not generate.
 * Route: /tasks and /tasks/:storyId. Leftover /path redirects here.
 */
export default function PathPage() {
  const { storyId: routeStoryId } = useParams();
  const [searchParams] = useSearchParams();
  const { currentProject, currentStory, setProjectAndStory, storyListVersion } = useApp();
  const navigate = useNavigate();
  const fromImport = searchParams.get('from') === 'import';

  const openedId = routeStoryId || currentStory?.id || null;
  const { storyId, loadedStory } = useSyncStoryContext({ routeStoryId: openedId });

  const [pickerOpen, setPickerOpen] = useState(false);
  const [projectStories, setProjectStories] = useState(null);
  const [projectStoriesLoading, setProjectStoriesLoading] = useState(false);
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [peopleBring, setPeopleBring] = useState(false);
  const [askNext, setAskNext] = useState(false);
  const [hangOpen, setHangOpen] = useState(false);
  const [gaps, setGaps] = useState(null);
  const [gapsLoading, setGapsLoading] = useState(false);
  const [gapsError, setGapsError] = useState(null);

  const activeProjectId = currentProject?.id || loadedStory?.project_id || null;
  const projectId = activeProjectId;
  const storyName = loadedStory?.name || currentStory?.name || null;

  useEffect(() => {
    if (!activeProjectId) { setProjectStories(null); return undefined; }
    let cancelled = false;
    setProjectStoriesLoading(true);
    storiesApi.list(activeProjectId)
      .then((res) => { if (!cancelled) setProjectStories(unwrapList(res)); })
      .catch(() => { if (!cancelled) setProjectStories([]); })
      .finally(() => { if (!cancelled) setProjectStoriesLoading(false); });
    return () => { cancelled = true; };
  }, [activeProjectId, storyListVersion]);

  useEffect(() => {
    if (error) return;
    if (routeStoryId) { setPickerOpen(false); return; }
    if (currentStory?.id) {
      setPickerOpen(false);
      navigate(pathRoomPath(currentStory.id), { replace: true });
      return;
    }
    if (!currentProject) { setPickerOpen(true); return; }
    if (!projectStories || projectStories.length === 0) return;
    const mostRecent = [...projectStories].sort(byRecency)[0];
    setProjectAndStory(currentProject, mostRecent);
    navigate(pathRoomPath(mostRecent.id), { replace: true });
  }, [
    routeStoryId,
    currentStory?.id,
    currentProject,
    projectStories,
    navigate,
    setProjectAndStory,
    error,
  ]);

  const loadInventory = useCallback(async () => {
    if (!storyId) return;
    setLoading(true);
    setError(null);
    try {
      const payload = await pathApi.get(storyId);
      setInventory(payload);
    } catch (err) {
      setError(err);
      setInventory(null);
    } finally {
      setLoading(false);
    }
  }, [storyId]);

  useEffect(() => { loadInventory(); }, [loadInventory]);

  useEffect(() => {
    setAskNext(false);
    setGaps(null);
    setGapsError(null);
  }, [storyId]);

  useEffect(() => {
    if (!askNext || !storyId) return undefined;
    let cancelled = false;
    setGapsLoading(true);
    setGapsError(null);
    pathApi.getGaps(storyId)
      .then((payload) => { if (!cancelled) setGaps(payload); })
      .catch((err) => {
        if (!cancelled) {
          setGapsError(err);
          setGaps(null);
        }
      })
      .finally(() => { if (!cancelled) setGapsLoading(false); });
    return () => { cancelled = true; };
  }, [askNext, storyId]);

  useEffect(() => {
    if (!storyId || !currentProject?.is_series) {
      setPeopleBring(false);
      return undefined;
    }
    let cancelled = false;
    bringIntoBookApi.list(storyId)
      .then((payload) => {
        if (!cancelled) setPeopleBring((payload?.offers || []).length > 0);
      })
      .catch(() => { if (!cancelled) setPeopleBring(false); });
    return () => { cancelled = true; };
  }, [storyId, currentProject?.is_series]);

  const handlePick = useCallback((project, story) => {
    setProjectAndStory(project, story);
    setPickerOpen(false);
    navigate(pathRoomPath(story.id));
  }, [setProjectAndStory, navigate]);

  const openDiscovery = useCallback(async (opener) => {
    const go = (sessionId) => {
      if (opener) {
        navigate(`/chat/${sessionId}`, { state: { pathGapOpener: opener } });
        return;
      }
      navigate(`/chat/${sessionId}`);
    };
    const sessionId = loadedStory?.discovery_session_id || currentStory?.discovery_session_id;
    if (sessionId) {
      go(sessionId);
      return;
    }
    if (!storyId) return;
    try {
      const ensured = await storiesApi.ensureDiscoverySession(storyId);
      const sessionId = ensured?.session_id || ensured?.id;
      if (sessionId) go(sessionId);
    } catch (err) {
      console.error('Failed to open Discovery:', err);
    }
  }, [loadedStory, currentStory, storyId, navigate]);

  const runAction = useCallback((action) => {
    if (!action) return;
    if (action === 'notes') {
      if (projectId && storyId) navigate(bookNotesPath(projectId, storyId));
      return;
    }
    if (action === 'discovery') {
      openDiscovery();
      return;
    }
    if (action === 'book_cards') {
      navigate('/bibles');
      return;
    }
    if (action === 'book_cards_theme') {
      const returnTo = rememberTasksReturn(storyId);
      navigate('/bibles?kind=theme', { state: { returnTo } });
      return;
    }
    if (action === 'book_cards_plot_theme') {
      const returnTo = rememberTasksReturn(storyId);
      navigate('/bibles?kind=plot', { state: { returnTo } });
      return;
    }
    if (action === 'continue_draft' || action === 'scene_writing') {
      navigate(continueDraftingPath(storyId));
      return;
    }
    if (action === 'review_draft') {
      const scene = inventory?.review_scene;
      if (scene?.id) {
        navigate(draftThisScenePath(storyId, { sceneId: scene.id, title: scene.name || '' }));
        return;
      }
      navigate(reviewDraftPath(storyId));
      return;
    }
    if (action === 'choose_framework') {
      setHangOpen(true);
      return;
    }
    if (action === 'plot_writeup') {
      navigate('/dossiers');
      return;
    }
    if (action === 'plottrack') {
      navigate(plottrackPath(storyId));
      return;
    }
    if (action === 'bring_into_book') {
      navigate(bringIntoBookPath(storyId));
    }
  }, [inventory, navigate, openDiscovery, projectId, storyId]);

  const rows = useMemo(() => {
    const incoming = inventory?.rows || [];
    return PATH_ROW_IDS.map((id) => {
      const live = incoming.find((row) => row.id === id) || {};
      const talkName = id === 'talk_or_notes'
        && (live.name === PATH_TALK_OR_NOTES_NAMES.fresh
          || live.name === PATH_TALK_OR_NOTES_NAMES.documents);
      const talkTeaching = id === 'talk_or_notes'
        && (live.teaching_line === PATH_TALK_OR_NOTES_TEACHING.fresh
          || live.teaching_line === PATH_TALK_OR_NOTES_TEACHING.documents);
      return {
        id,
        name: talkName ? live.name : PATH_ROW_NAMES[id],
        teaching_line: talkTeaching ? live.teaching_line : PATH_TEACHING_LINES[id],
        state: live.state || (id === 'scene_prose' ? 'later' : 'missing'),
        state_label: PATH_STATE_LABELS[live.state] || live.state_label || (
          id === 'scene_prose' ? PATH_STATE_LABELS.later : PATH_STATE_LABELS.missing
        ),
        extra_line: live.extra_line || null,
        action: live.action || null,
      };
    });
  }, [inventory]);

  const nextRow = rows.find((row) => (
    row.id !== 'scene_prose' && (row.state === 'missing' || row.state === 'will_invent')
  )) || null;
  const nextAction = rowAction(nextRow, { fromImport, peopleBring });
  const allPresent = Boolean(inventory?.all_present) || (
    rows.slice(0, 6).every((row) => row.state === 'present')
  );
  const unansweredGroups = useMemo(() => (
    (gaps?.groups || [])
      .map((group) => ({
        ...group,
        questions: (group.questions || []).filter((q) => q.status === 'unanswered'),
      }))
      .filter((group) => group.questions.length > 0)
  ), [gaps]);

  if (!storyId) {
    if (currentProject) {
      if (projectStoriesLoading || projectStories === null || projectStories.length > 0) {
        return <div className="path-page path-page--state">Opening Tasks…</div>;
      }
      return (
        <div className="path-page">
          <div className="path-page--state">
            <h2>No stories yet</h2>
            <p>{currentProject.name || 'This project'} doesn't have any stories yet.</p>
          </div>
        </div>
      );
    }
    return (
      <div className="path-page">
        <div className="path-page--state">
          <button className="path-page__action" onClick={() => setPickerOpen(true)}>
            Choose a story
          </button>
        </div>
        <StoryPicker
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onPick={handlePick}
          currentStoryId={storyId}
          title="Open a story on Tasks"
        />
      </div>
    );
  }

  if (loading && !inventory) {
    return <div className="path-page path-page--state">Opening Tasks…</div>;
  }

  if (error && !inventory) {
    return (
      <div className="path-page">
        <div className="path-page--state">
          <h2>Could not open Tasks</h2>
          <p>The inventory did not load. Try again.</p>
          <button className="path-page__action" onClick={loadInventory}>Try again</button>
        </div>
      </div>
    );
  }

  const readiness = Array.isArray(inventory?.readiness) ? inventory.readiness : [];
  const allReady = readiness.length > 0 && readiness.every((item) => item.state === 'ready');
  const readinessHeading = allReady ? 'Ready to generate' : WORKSHOP_STATUS_NOT_READY;

  return (
    <div className="path-page">
      <header className="path-page__header">
        <p className="path-page__kicker">Tasks</p>
        <h1 className="path-page__title">{storyName || 'This book'}</h1>
        <div className="path-page__rule" aria-hidden="true">
          <span className="path-page__rule-line" />
          <span className="path-page__rule-line path-page__rule-line--faint" />
        </div>
        <div className="path-page__next">
          <h2 className="path-page__next-label">{PATH_HEADER}</h2>
          {allPresent ? (
            <p className="path-page__all-present">{PATH_ALL_PRESENT}</p>
          ) : nextRow ? (
            <button
              type="button"
              className="path-page__action path-page__action--primary"
              onClick={() => runAction(nextAction)}
            >
              {PATH_ACTION_LABELS[nextAction] || PATH_ROW_NAMES[nextRow.id]}
            </button>
          ) : null}
        </div>
      </header>

      {readiness.length > 0 ? (
        <section className="path-readiness" aria-label={readinessHeading} data-testid="path-readiness">
          <h2 className="path-page__next-label">{readinessHeading}</h2>
          <ol className="path-page__map">
            {readiness.map((item) => (
              <li
                key={item.id || item.name}
                className="path-row"
                data-state={item.state || 'not_ready'}
              >
                <div className="path-row__meta">
                  <h3 className="path-row__name">{item.name}</h3>
                  <span className="path-row__state">{item.state_label}</span>
                </div>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      <ol className="path-page__map">
        {rows.map((row, index) => {
          const action = rowAction(row, { fromImport, peopleBring });
          const later = row.state === 'later';
          return (
            <li
              key={row.id}
              className={`path-row${later ? ' path-row--later' : ''}${nextRow?.id === row.id ? ' path-row--next' : ''}`}
            >
              <div className="path-row__meta">
                <span className="path-row__no">{catalogNo(index)}</span>
                <h3 className="path-row__name">{row.name}</h3>
                <span className="path-row__state">{row.state_label}</span>
              </div>
              <p className="path-row__teaching">{row.teaching_line}</p>
              {row.extra_line && (
                <p className="path-row__extra">{row.extra_line}</p>
              )}
              {row.id === 'scene_prose' && !later ? (
                <div className="path-row__actions">
                  <button
                    type="button"
                    className="path-page__action"
                    onClick={() => runAction('continue_draft')}
                  >
                    {PATH_ACTION_LABELS.continue_draft}
                  </button>
                  <button
                    type="button"
                    className="path-page__action"
                    onClick={() => runAction('review_draft')}
                  >
                    {PATH_ACTION_LABELS.review_draft}
                  </button>
                </div>
              ) : action && !later ? (
                <button
                  type="button"
                  className="path-page__action"
                  onClick={() => runAction(action)}
                >
                  {PATH_ACTION_LABELS[action] || 'Open'}
                </button>
              ) : null}
            </li>
          );
        })}
      </ol>

      <section className="path-gaps" aria-label={PATH_GAPS_CONTROL}>
        <label className="path-gaps__control">
          <input
            type="checkbox"
            checked={askNext}
            onChange={(e) => setAskNext(e.target.checked)}
          />
          {PATH_GAPS_CONTROL}
        </label>
        {askNext && !gapsLoading && !gapsError && gaps && unansweredGroups.length === 0 && (
          <p className="path-row__teaching">{PATH_GAPS_EMPTY}</p>
        )}
        {askNext && unansweredGroups.length > 0 && (
          <ul className="path-gaps__groups">
            {unansweredGroups.map((group) => (
              <li key={group.id} className="path-row">
                <div className="path-row__meta">
                  <h3 className="path-row__name">
                    {group.label}
                    {group.subject?.name ? ` — ${group.subject.name}` : ''}
                  </h3>
                </div>
                <ul className="path-gaps__questions">
                  {group.questions.map((q) => (
                    <li key={q.id} className="path-row__teaching">{q.label}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="path-page__action"
                  onClick={() => openDiscovery(group.opener)}
                >
                  {PATH_GAPS_ACTION}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <span className="path-page__mark" aria-hidden="true">
        <AtlasIcon name="corridor" size={18} />
      </span>

      <BookStructureDoor
        storyId={storyId}
        open={hangOpen}
        onOpenChange={setHangOpen}
        showTrigger={false}
      />
    </div>
  );
}
